import { Link } from "react-router-dom"
import Icon from "@/components/icon"
import { generateGradientFromId } from "@/utils/gradient"
import { buildCourseHref } from "@/utils/course"
import Image from "@/components/image"
import CourseInstallmentBadge from "@/components/course-installment-badge"
import { ELEMENT_TYPES } from "@/features/analytics"
import { fa, resolvePrice } from "@/features/course/components/course-format"

interface MiniApp {
    uuid: string
    title: string
    cover_url: string | null
    cover_thumb_url?: string | null
}

interface Props {
    title: string
    sessionsCount: number
    studentsCount: number
    thumbnail: string | null
    courseId?: number
    miniApp?: MiniApp | null
    miniAppUuid?: string | null
    description?: string
    rating?: number | null
    price?: number | null
    specialPrice?: number | null
    isFree?: boolean
    /** Position in the list it is rendered in — `slot_index` on click/view
     *  events. The `section` comes from the list's own wrapper. */
    slotIndex?: number
}

function CourseCard({ title, sessionsCount, studentsCount, thumbnail, courseId, miniApp, miniAppUuid, description, rating, price, specialPrice, isFree, slotIndex }: Props) {
    const showRating = typeof rating === "number" && rating > 0
    const uuid = miniApp?.uuid ?? miniAppUuid
    const courseHref = courseId ? buildCourseHref(courseId, uuid) : '#'

    const hasPrice = typeof price === "number"
    const priceInfo = hasPrice ? resolvePrice(price, specialPrice ?? null) : null
    const free = isFree || (priceInfo?.isFree ?? false)

    return (
        <article className="w-full rounded-2xl bg-background border border-default-200 p-2.5 flex flex-col gap-3">

            <Link
                to={courseHref}
                className="flex h-36 gap-3 items-stretch"
                data-track-type={ELEMENT_TYPES.courseCard}
                data-track-id={courseId}
                data-track-slot={slotIndex}
                data-track-target="course"
                data-track-course-id={courseId}
                data-track-miniapp={uuid ?? undefined}
            >
                <div
                    className="relative h-full aspect-square shrink-0 grow-0 rounded-xl overflow-hidden border border-default-100"
                    style={!thumbnail && courseId ? { background: generateGradientFromId(courseId) } : undefined}
                >
                    {thumbnail && (
                        <Image
                            src={thumbnail}
                            alt={title}
                            sizes="144px"
                            className="absolute inset-0 size-full object-cover"
                            loading="lazy"
                            decoding="async"
                        />
                    )}
                    {priceInfo ? (
                        <CourseInstallmentBadge
                            finalPrice={priceInfo.finalPrice}
                            isFree={free}
                        />
                    ) : null}
                </div>

                <div className="flex-1 min-w-0 flex flex-col gap-1.5">

                    <h3 className="text-sm font-bold text-default-900 leading-6 line-clamp-2">
                        {title}
                    </h3>

                    {description ? (
                        <p className="text-xs leading-5 font-light text-default-500 line-clamp-1">
                            {description}
                        </p>
                    ) : null}

                    <div className="flex items-center gap-3 text-xs text-default-500">
                        {showRating ? (
                            <span className="flex items-center gap-1 font-bold text-default-900">
                                {rating!.toLocaleString("fa-IR", { maximumFractionDigits: 1 })}
                                <Icon name="star-fill" size={14} className="text-yellow-500" />
                            </span>
                        ) : null}

                        {studentsCount != null && studentsCount > 0 ? (
                            <span className="flex items-center gap-1">
                                <Icon name="users-fill" size={14} />
                                {studentsCount.toLocaleString("fa-IR")}
                            </span>
                        ) : null}

                        {sessionsCount != null ? (
                            <span className="flex items-center gap-1">
                                <Icon name="lessons-fill" size={14} />
                                {sessionsCount.toLocaleString("fa-IR")} جلسه
                            </span>
                        ) : null}
                    </div>

                    {/* {miniApp && (
                        <span className="inline-flex items-center gap-1 self-start text-[11px] text-default-500 truncate max-w-full">
                            {miniApp.title}
                        </span>
                    )} */}

                    {priceInfo ? (
                        <div className="flex justify-end self-end mt-auto">
                            {free ? (
                                <span className="inline-flex items-center gap-1 text-sm font-bold text-success-500">
                                    <Icon name="free-price-fill" size={16} />
                                    رایگان
                                </span>
                            ) : (
                                <div className="flex flex-col items-start gap-0.5">
                                    <div className="flex items-center gap-2">
                                        {priceInfo.originalPrice ? (
                                            <span className="text-xs text-default-400 line-through">
                                                {fa(priceInfo.originalPrice)}
                                            </span>
                                        ) : null}
                                        {priceInfo.discountPercent > 0 ? (
                                            <span className="shrink-0 rounded-md bg-danger text-white text-[10px] font-bold px-1.5 pt-0.5 pb-.25">
                                                {fa(priceInfo.discountPercent)}٪
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-sm font-extrabold text-default-900">
                                            {fa(priceInfo.finalPrice)}
                                        </span>
                                        <span className="text-[10px] text-default-500">
                                            تومان
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            </Link>
        </article>
    )
}

export function CourseCardSkeleton({ showMiniApp = false }: { showMiniApp?: boolean }) {
    return (
        <article className="w-full rounded-2xl bg-background border border-default-200 p-2.5 flex flex-col gap-3">
            <div className="flex h-36 gap-3 items-stretch">
                <div className="h-full aspect-square shrink-0 rounded-xl bg-default-200 animate-pulse" />

                <div className="flex-1 min-w-0 flex flex-col gap-2">
                    <div className="h-3 bg-default-200 rounded animate-pulse w-1/4" />
                    <div className="h-4 bg-default-200 rounded animate-pulse w-4/5" />
                    <div className="h-4 bg-default-200 rounded animate-pulse w-3/5" />
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-3 bg-default-200 rounded animate-pulse" />
                        <div className="w-10 h-3 bg-default-200 rounded animate-pulse" />
                    </div>
                    {showMiniApp && (
                        <div className="h-3 bg-default-200 rounded animate-pulse w-1/3" />
                    )}
                    <div className="mt-auto pt-1 space-y-1">
                        <div className="h-3 bg-default-200 rounded animate-pulse w-1/3" />
                        <div className="h-5 bg-default-200 rounded animate-pulse w-1/2" />
                    </div>
                </div>
            </div>
        </article>
    )
}

export default CourseCard
